import { useEffect, useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api, { setAuthToken } from "@/lib/api";
import { toast } from "sonner";
import { Mail, ArrowRight, MessageSquare, KeyRound, Phone } from "lucide-react";

/**
 * Login page — three flows behind a small mode switch:
 *
 *  1. "phone" (default) — Phone-first SMS login. User types phone → we
 *     find the matching account and Twilio-Verify-text a code. Fastest
 *     path for any returning employee with a phone on file.
 *  2. "email" — First-time setup OR phone change. User types work email
 *     → we email a 6-digit code → user enters code + the phone they
 *     want to use → backend saves the phone and signs them in. After
 *     this, mode 1 works for them.
 *  3. "password" — Fallback escape hatch (kept hidden behind a small
 *     link) for users who'd rather sign in the old-fashioned way.
 */
export default function Login() {
  const { user, login, setUser } = useAuth();
  const navigate = useNavigate();

  const [mode, setMode] = useState("phone"); // "phone" | "email" | "password"
  const [smsEnabled, setSmsEnabled] = useState(true);

  // Phone flow
  const [phone, setPhone] = useState("");
  const [phoneStep, setPhoneStep] = useState("input"); // "input" | "code"
  const [phoneHint, setPhoneHint] = useState("");
  const [phoneCode, setPhoneCode] = useState("");

  // Email setup flow
  const [email, setEmail] = useState("");
  const [emailStep, setEmailStep] = useState("input"); // "input" | "verify"
  const [emailCode, setEmailCode] = useState("");
  const [setupPhone, setSetupPhone] = useState("");
  const [isNewSignup, setIsNewSignup] = useState(false);

  // Password fallback
  const [password, setPassword] = useState("");

  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/auth/sms/status")
      .then((r) => {
        const enabled = !!r.data?.enabled;
        setSmsEnabled(enabled);
        if (!enabled) setMode("password");
      })
      .catch(() => { setMode("password"); });
  }, []);

  if (user) return <Navigate to="/inbox" replace />;

  const adoptSession = (data) => {
    if (data?.token) setAuthToken(data.token);
    if (data?.user) setUser(data.user);
  };

  // ---------- Phone-first SMS ----------
  const requestPhoneCode = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    const v = (phone || "").trim();
    if (v.replace(/\D/g, "").length < 7) {
      setError("That phone number doesn't look right. Use a 10-digit US number or full +country code.");
      return;
    }
    setSubmitting(true); setError("");
    try {
      const { data } = await api.post("/auth/sms/request-by-phone", { phone: v });
      setPhoneHint(data?.phone_last4 ? `•••• ${data.phone_last4}` : "");
      setPhoneStep("code");
      toast.success("Code sent — check your phone.");
    } catch (err) {
      setError(err.response?.data?.detail || "Couldn't send the code. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const verifyPhoneCode = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    const v = (phone || "").trim();
    const c = (phoneCode || "").trim();
    if (c.length < 4) { setError("Enter the code from your phone."); return; }
    setSubmitting(true); setError("");
    try {
      const { data } = await api.post("/auth/sms/verify-by-phone", { phone: v, code: c });
      adoptSession(data);
      navigate("/inbox");
    } catch (err) {
      setError(err.response?.data?.detail || "Invalid or expired code.");
    } finally {
      setSubmitting(false);
    }
  };

  // ---------- First-time email setup ----------
  const requestEmailCode = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    const v = (email || "").trim().toLowerCase();
    if (!v || !v.includes("@")) { setError("Enter your work email."); return; }
    setSubmitting(true); setError("");
    try {
      const { data } = await api.post("/auth/email-code/request", { email: v });
      setIsNewSignup(!!data?.is_new);
      setEmailStep("verify");
      toast.success(data?.message || "Check your inbox — we sent a 6-digit code.");
    } catch (err) {
      setError(err.response?.data?.detail || "Couldn't send the email. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const verifyEmailCode = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    const v = (email || "").trim().toLowerCase();
    const c = (emailCode || "").trim();
    const p = (setupPhone || "").trim();
    if (c.length !== 6) { setError("Enter the 6-digit code from the email."); return; }
    if (p.replace(/\D/g, "").length < 7) { setError("Enter the phone number you want to use for SMS sign-in."); return; }
    setSubmitting(true); setError("");
    try {
      const { data } = await api.post("/auth/email-code/verify", { email: v, code: c, phone: p });
      adoptSession(data);
      toast.success("Phone saved — you're signed in.");
      navigate("/inbox");
    } catch (err) {
      setError(err.response?.data?.detail || "Couldn't verify the code.");
    } finally {
      setSubmitting(false);
    }
  };

  // ---------- Password fallback ----------
  const onPasswordSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true); setError("");
    const res = await login(email, password);
    setSubmitting(false);
    if (res.ok) navigate("/inbox");
    else setError(res.error);
  };

  const switchMode = (m) => {
    setMode(m); setError("");
    setPhoneStep("input"); setPhoneCode("");
    setEmailStep("input"); setEmailCode(""); setSetupPhone("");
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#fafafb]">
      {/* Left: form */}
      <div className="flex flex-col justify-between p-10 lg:p-16">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-[#0033CC] flex items-center justify-center rounded-sm">
            <Mail className="w-4 h-4 text-white" strokeWidth={2} />
          </div>
          <span className="font-heading text-lg font-medium tracking-tight">InboxHQ</span>
        </div>

        <div className="max-w-sm w-full mx-auto lg:mx-0 animate-fade-in">
          <p className="text-xs font-semibold tracking-[0.1em] uppercase text-zinc-500 mb-3">Sign in</p>
          <h1 className="font-heading text-3xl sm:text-4xl font-medium tracking-tight mb-2">
            The control room for your team&apos;s inbox.
          </h1>
          <p className="text-sm text-zinc-600 mb-8 leading-relaxed">
            One Gmail connection. 100 agents. Zero quota headaches.
          </p>

          {/* Phone mode */}
          {mode === "phone" && phoneStep === "input" && (
            <form onSubmit={requestPhoneCode} className="space-y-5" data-testid="phone-request-form" noValidate>
              <div>
                <Label htmlFor="phone" className="text-xs font-semibold tracking-[0.05em] uppercase text-zinc-500">Phone number</Label>
                <Input
                  id="phone"
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  placeholder="(555) 123-4567"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") requestPhoneCode(e); }}
                  className="mt-1.5 h-11 rounded-md border-zinc-300 focus-visible:ring-[#0033CC]"
                  required
                  autoFocus
                  data-testid="phone-input"
                />
                <p className="text-xs text-zinc-500 mt-1.5">We&apos;ll text a 6-digit code to your phone.</p>
              </div>

              {error && <ErrorBox message={error} />}

              <Button
                type="submit"
                disabled={submitting}
                className="w-full h-11 bg-[#0033CC] hover:bg-[#002299] text-white rounded-md font-medium"
                data-testid="phone-send-button"
              >
                {submitting ? "Sending…" : "Send code"}
                <MessageSquare className="w-4 h-4 ml-1.5" strokeWidth={2} />
              </Button>

              <div className="text-center pt-2">
                <button
                  type="button"
                  onClick={() => switchMode("email")}
                  className="text-xs text-zinc-500 hover:text-[#0033CC] underline-offset-2 hover:underline"
                  data-testid="open-email-setup"
                >
                  First time, or new phone? Sign up / sign in with your work email →
                </button>
              </div>
            </form>
          )}

          {mode === "phone" && phoneStep === "code" && (
            <form onSubmit={verifyPhoneCode} className="space-y-5" data-testid="phone-verify-form" noValidate>
              <div>
                <Label className="text-xs font-semibold tracking-[0.05em] uppercase text-zinc-500">Enter the 6-digit code</Label>
                {phoneHint && (
                  <p className="text-xs text-zinc-500 mt-1">Sent to <span className="font-mono">{phoneHint}</span></p>
                )}
                <Input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  autoComplete="one-time-code"
                  maxLength={6}
                  value={phoneCode}
                  onChange={(e) => setPhoneCode(e.target.value.replace(/[^0-9]/g, ""))}
                  onKeyDown={(e) => { if (e.key === "Enter") verifyPhoneCode(e); }}
                  className="mt-1.5 h-12 rounded-md border-zinc-300 focus-visible:ring-[#0033CC] text-center text-2xl font-mono tracking-[0.4em]"
                  required
                  autoFocus
                  data-testid="phone-code-input"
                />
              </div>

              {error && <ErrorBox message={error} />}

              <Button
                type="submit"
                disabled={submitting || phoneCode.length < 4}
                className="w-full h-11 bg-[#0033CC] hover:bg-[#002299] text-white rounded-md font-medium"
                data-testid="phone-verify-button"
              >
                {submitting ? "Verifying…" : "Verify & sign in"}
                <KeyRound className="w-4 h-4 ml-1.5" strokeWidth={2} />
              </Button>

              <button
                type="button"
                onClick={() => { setPhoneStep("input"); setPhoneCode(""); setError(""); }}
                className="w-full text-xs text-zinc-500 hover:text-zinc-900"
                data-testid="phone-back-button"
              >
                ← Use a different number
              </button>
            </form>
          )}

          {/* Email setup mode */}
          {mode === "email" && emailStep === "input" && (
            <form onSubmit={requestEmailCode} className="space-y-5" data-testid="email-request-form" noValidate>
              <div>
                <Label htmlFor="setup-email" className="text-xs font-semibold tracking-[0.05em] uppercase text-zinc-500">Work email</Label>
                <Input
                  id="setup-email"
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  placeholder="you@choicecapitalsolutions.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") requestEmailCode(e); }}
                  className="mt-1.5 h-11 rounded-md border-zinc-300 focus-visible:ring-[#0033CC]"
                  required
                  autoFocus
                  data-testid="email-input"
                />
                <p className="text-xs text-zinc-500 mt-1.5">Any choicecapitalsolutions.com email works — new hire or existing. We&apos;ll email you a 6-digit code and save your phone for SMS sign-in next time.</p>
              </div>

              {error && <ErrorBox message={error} />}

              <Button
                type="submit"
                disabled={submitting}
                className="w-full h-11 bg-[#0033CC] hover:bg-[#002299] text-white rounded-md font-medium"
                data-testid="email-send-button"
              >
                {submitting ? "Sending…" : "Email me a code"}
                <Mail className="w-4 h-4 ml-1.5" strokeWidth={2} />
              </Button>

              <div className="text-center pt-2">
                <button
                  type="button"
                  onClick={() => switchMode("phone")}
                  className="text-xs text-zinc-500 hover:text-[#0033CC] underline-offset-2 hover:underline"
                  data-testid="back-to-phone"
                >
                  ← I already have a phone on file
                </button>
              </div>
            </form>
          )}

          {mode === "email" && emailStep === "verify" && (
            <form onSubmit={verifyEmailCode} className="space-y-5" data-testid="email-verify-form" noValidate>
              <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800" data-testid="email-setup-banner">
                {isNewSignup
                  ? "Welcome! Creating your account — set your phone to finish."
                  : "Account found — set your phone to enable SMS sign-in next time."}
              </div>
              <div>
                <Label className="text-xs font-semibold tracking-[0.05em] uppercase text-zinc-500">Email code</Label>
                <p className="text-xs text-zinc-500 mt-1">Check <span className="font-mono">{email}</span> for the 6-digit code.</p>
                <Input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  autoComplete="one-time-code"
                  maxLength={6}
                  value={emailCode}
                  onChange={(e) => setEmailCode(e.target.value.replace(/[^0-9]/g, ""))}
                  className="mt-1.5 h-12 rounded-md border-zinc-300 focus-visible:ring-[#0033CC] text-center text-2xl font-mono tracking-[0.4em]"
                  required
                  autoFocus
                  data-testid="email-code-input"
                />
              </div>

              <div>
                <Label htmlFor="setup-phone" className="text-xs font-semibold tracking-[0.05em] uppercase text-zinc-500">Your phone number</Label>
                <Input
                  id="setup-phone"
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  placeholder="(555) 123-4567"
                  value={setupPhone}
                  onChange={(e) => setSetupPhone(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") verifyEmailCode(e); }}
                  className="mt-1.5 h-11 rounded-md border-zinc-300 focus-visible:ring-[#0033CC]"
                  required
                  data-testid="setup-phone-input"
                />
                <p className="text-xs text-zinc-500 mt-1.5">We&apos;ll save this so you can sign in with SMS next time.</p>
              </div>

              {error && <ErrorBox message={error} />}

              <Button
                type="submit"
                disabled={submitting || emailCode.length !== 6 || !setupPhone}
                className="w-full h-11 bg-[#0033CC] hover:bg-[#002299] text-white rounded-md font-medium"
                data-testid="email-verify-button"
              >
                {submitting ? "Saving…" : (isNewSignup ? "Create account & sign in" : "Save & sign in")}
                <Phone className="w-4 h-4 ml-1.5" strokeWidth={2} />
              </Button>

              <button
                type="button"
                onClick={() => { setEmailStep("input"); setEmailCode(""); setSetupPhone(""); setError(""); }}
                className="w-full text-xs text-zinc-500 hover:text-zinc-900"
                data-testid="email-back-button"
              >
                ← Use a different email
              </button>
            </form>
          )}

          {/* Password fallback mode */}
          {mode === "password" && (
            <form onSubmit={onPasswordSubmit} className="space-y-5" data-testid="login-form">
              <div>
                <Label htmlFor="pw-email" className="text-xs font-semibold tracking-[0.05em] uppercase text-zinc-500">Work email</Label>
                <Input
                  id="pw-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-1.5 h-11 rounded-md border-zinc-300 focus-visible:ring-[#0033CC]"
                  required
                  data-testid="login-email-input"
                />
              </div>
              <div>
                <div className="flex items-baseline justify-between">
                  <Label htmlFor="password" className="text-xs font-semibold tracking-[0.05em] uppercase text-zinc-500">Password</Label>
                  <Link to="/forgot-password" className="text-xs text-[#0033CC] hover:underline" data-testid="forgot-password-link">
                    Forgot?
                  </Link>
                </div>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="mt-1.5 h-11 rounded-md border-zinc-300 focus-visible:ring-[#0033CC]"
                  required
                  data-testid="login-password-input"
                />
              </div>

              {error && <ErrorBox message={error} />}

              <Button type="submit" disabled={submitting} className="w-full h-11 bg-[#0033CC] hover:bg-[#002299] text-white rounded-md font-medium" data-testid="login-submit-button">
                {submitting ? "Signing in…" : "Sign in"}
                <ArrowRight className="w-4 h-4 ml-1.5" strokeWidth={2} />
              </Button>
            </form>
          )}

          {/* Mode footer link */}
          {smsEnabled && (
            <div className="mt-6 text-center">
              {mode !== "password" ? (
                <button type="button" onClick={() => switchMode("password")} className="text-xs text-zinc-400 hover:text-zinc-700" data-testid="open-password-mode">
                  Use a password instead
                </button>
              ) : (
                <button type="button" onClick={() => switchMode("phone")} className="text-xs text-zinc-400 hover:text-zinc-700" data-testid="back-to-sms">
                  ← Back to SMS sign-in
                </button>
              )}
            </div>
          )}
        </div>

        <div className="text-xs text-zinc-400">© 2026 Internal Inbox — Shared inbox infrastructure.</div>
      </div>

      {/* Right: decorative panel */}
      <div className="hidden lg:flex bg-[#09090B] text-white p-16 flex-col justify-between relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.06]" style={{ backgroundImage: "radial-gradient(circle at 1px 1px, white 1px, transparent 0)", backgroundSize: "24px 24px" }} />
        <div className="relative">
          <p className="text-xs font-semibold tracking-[0.15em] uppercase text-[#7CA0FF] mb-4">// Architecture</p>
          <h2 className="font-heading text-3xl font-medium tracking-tight max-w-md leading-tight">
            One sync. One source of truth. One hundred agents.
          </h2>
        </div>
        <div className="relative grid grid-cols-2 gap-px bg-white/10 border border-white/10">
          {[
            { k: "01", t: "OAuth once", d: "Connect Gmail at the org level — never per agent." },
            { k: "02", t: "Background sync", d: "Workers mirror threads with backoff & retries." },
            { k: "03", t: "Read from mirror", d: "100 agents read MongoDB, not Gmail." },
            { k: "04", t: "Outbound queue", d: "Replies dispatched through a single rate-limited pipe." },
          ].map((b) => (
            <div key={b.k} className="bg-[#09090B] p-6">
              <p className="font-mono text-xs text-[#7CA0FF] mb-2">{b.k}</p>
              <p className="font-heading text-base font-medium mb-1">{b.t}</p>
              <p className="text-xs text-zinc-400 leading-relaxed">{b.d}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ErrorBox({ message }) {
  return (
    <div className="text-sm text-red-600 border border-red-200 bg-red-50 px-3 py-2 rounded-md" data-testid="login-error">
      {message}
    </div>
  );
}
